// /////////////////////////////////////////////////////////////
// [ファイル] AgentDetail.tsx
// [概要] エージェント設定ダイアログの右ペイン (定義の詳細)。閲覧と編集を同じ場所で切り替える。
//        - 閲覧: 区分と注入文字数、所属グループ、見える人、ステータス (課題)、有効、本文、最終更新
//        - 編集: 名前・説明・本文・所属グループ・共有範囲・ステータス・有効 (保存は親の SaveBar)
//        - 競合 (楽観ロック違反) は最新の内容と自分の内容を行単位の差分で並べて選ばせる
// /////////////////////////////////////////////////////////////
import React, { useEffect, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import type { AgentGroup, AgentItem, AgentScope, AgentType, IssueStatus } from '../../hooks/useAssistantAgents';
import {
  AGENT_TYPES,
  BODY_LABEL,
  BodyView,
  DESC_LABEL,
  DetailHeader,
  DiffLines,
  INJECTION_LIMIT,
  ISSUE_STATUS,
  ISSUE_STATUS_ORDER,
  KIND_LABEL,
  MetaRow,
  type StaffOption,
  charsOf,
  formatDateTime,
  isInjected,
} from './agentSettingsShared';

export interface AgentEditForm {
  id: string; // '' = 新規
  type: AgentType;
  scope: AgentScope;
  name: string;
  description: string;
  body: string;
  enabled: boolean;
  status: IssueStatus;
  members: StaffOption[]; // 共通の shared のみ (作成者・本人は自動で含まれる)
  groupId: string;
  expectedUpdatedAt: number; // 楽観ロック (編集開始時の updated_at)
}

interface Props {
  item: AgentItem | null; // 新規作成中は null
  form: AgentEditForm | null; // null = 閲覧
  onFormChange: (next: AgentEditForm) => void;
  conflict: AgentItem | null;
  saving: boolean;
  busy: boolean; // 行の付け替え・削除・スイッチ更新中
  editable: boolean;
  groups: AgentGroup[]; // 所属候補 (有効なグループ)
  groupById: Map<string, AgentGroup>;
  staffOptions: StaffOption[];
  currentUserEmail: string;
  formatMembers: (emails: string[] | undefined) => string;
  resolve: (email: string) => string;
  narrow: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: () => void;
  onSetStatus: (status: IssueStatus) => void;
  onAdoptLatest: () => void;
  onKeepMine: () => void;
}

export default function AgentDetail({
  item,
  form,
  onFormChange,
  conflict,
  saving,
  busy,
  editable,
  groups,
  groupById,
  staffOptions,
  currentUserEmail,
  formatMembers,
  resolve,
  narrow,
  onBack,
  onEdit,
  onDelete,
  onToggleEnabled,
  onSetStatus,
  onAdoptLatest,
  onKeepMine,
}: Props) {
  const nameRef = useRef<HTMLInputElement>(null);
  // 編集開始・新規作成の直後は名前欄へフォーカスする
  const editingKey = form ? `${form.id}` : null;
  useEffect(() => {
    if (editingKey !== null) {
      const t = window.setTimeout(() => nameRef.current?.focus({ preventScroll: true }), 80);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [editingKey]);

  if (saving) {
    return <LoadingView message="定義を保存しています..." height="100%" size={40} />;
  }

  // --- 編集 ----------------------------------------------------------------
  if (form) {
    const set = <K extends keyof AgentEditForm>(key: K, value: AgentEditForm[K]) => onFormChange({ ...form, [key]: value });
    const isNew = form.id === '';
    const group = form.groupId ? groupById.get(form.groupId) : undefined;
    // shared → personal は作成者のみ (他メンバーの手元から消えるため。バックエンドと同じ制約)
    const scopeLocked = !isNew && item?.scope === 'shared' && item.user_id !== currentUserEmail;
    const count = charsOf(form);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
        <DetailHeader
          narrow={narrow}
          onBack={onBack}
          kind={isNew ? `新しい${KIND_LABEL[form.type]}` : `${KIND_LABEL[form.type]} を編集`}
          title={
            <TextField
              inputRef={nameRef}
              size="small"
              fullWidth
              placeholder="名前"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              inputProps={{ maxLength: 60, 'aria-label': '名前', style: { fontWeight: 700, fontSize: '0.95rem' } }}
              sx={{ mt: 0.25, '& .MuiInputBase-root': { bgcolor: '#fff' } }}
            />
          }
        />

        {conflict && (
          <Alert severity="warning" sx={{ fontSize: '0.76rem', '& .MuiAlert-message': { minWidth: 0, flex: 1 } }}>
            <b>他の利用者または AI が先に更新しました</b>
            {conflict.updated_at ? ` (${formatDateTime(conflict.updated_at)})` : ''}。差分を確認して、どちらを採るか選んでください。
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1 }}>
              <DiffLines label="最新の内容" text={conflict.body ?? ''} other={form.body} />
              <DiffLines label="自分の内容" text={form.body} other={conflict.body ?? ''} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
              <Button size="small" color="inherit" variant="outlined" onClick={onAdoptLatest} sx={{ textTransform: 'none', fontSize: '0.74rem' }}>
                最新の内容を取り込む
              </Button>
              <Button size="small" color="inherit" variant="outlined" onClick={onKeepMine} sx={{ textTransform: 'none', fontSize: '0.74rem' }}>
                自分の内容で上書き
              </Button>
            </Box>
          </Alert>
        )}

        {isNew && (
          <FormControl size="small" sx={{ maxWidth: 220 }}>
            <InputLabel id="agent-new-type">種別</InputLabel>
            <Select
              labelId="agent-new-type"
              label="種別"
              value={form.type}
              onChange={(e) => set('type', e.target.value as AgentType)}
              MenuProps={{ disableScrollLock: true }}
            >
              {AGENT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {KIND_LABEL[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label={DESC_LABEL[form.type]}
          size="small"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          inputProps={{ maxLength: 300 }}
          sx={{ '& .MuiInputBase-root': { bgcolor: '#fff' } }}
        />
        <TextField
          label={BODY_LABEL[form.type]}
          size="small"
          multiline
          minRows={6}
          maxRows={18}
          value={form.body}
          onChange={(e) => set('body', e.target.value)}
          helperText={
            form.type === 'workflow'
              ? `${form.body.length.toLocaleString()} 字 (実行時に個別送信。注入合計には含まれません)`
              : `${count.toLocaleString()} 字を注入 (名前 + 説明 + 本文)${
                  count > INJECTION_LIMIT / 4 ? ` ・ 全定義の合計が ${INJECTION_LIMIT.toLocaleString()} 字を超えると回答精度が落ちます` : ''
                }`
          }
          sx={{ '& .MuiInputBase-root': { bgcolor: '#fff' } }}
        />

        <MetaRow label="所属グループ">
          <FormControl size="small" sx={{ minWidth: 200, '& .MuiInputBase-root': { bgcolor: '#fff' } }}>
            <Select
              value={form.groupId}
              onChange={(e) => set('groupId', String(e.target.value))}
              displayEmpty
              MenuProps={{ disableScrollLock: true }}
              inputProps={{ 'aria-label': '所属グループ' }}
            >
              <MenuItem value="">共通 (すべてのチャットで渡す)</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MetaRow>

        {/* 共有設定は共通 (グループ無し) の定義だけ。グループ付きはグループの共有先に従う */}
        <MetaRow label="共有範囲">
          {form.groupId ? (
            <Typography variant="caption" color="text.secondary">
              グループ「{group?.name ?? ''}」の設定に従います
              {group ? (group.scope === 'shared' ? ` (${formatMembers(group.shared_with)})` : ' (個人グループ)') : ''}
            </Typography>
          ) : (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={form.scope}
              onChange={(_, v) => {
                if (v) set('scope', v);
              }}
              disabled={scopeLocked}
              sx={{ bgcolor: '#fff' }}
            >
              <ToggleButton value="personal" sx={{ textTransform: 'none', px: 1.5, py: 0.375, fontSize: '0.74rem' }}>
                <Icons.Person sx={{ fontSize: 15, mr: 0.5 }} /> 自分だけ
              </ToggleButton>
              <ToggleButton value="shared" sx={{ textTransform: 'none', px: 1.5, py: 0.375, fontSize: '0.74rem' }}>
                <Icons.Groups sx={{ fontSize: 15, mr: 0.5 }} /> 共有
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </MetaRow>
        {!form.groupId && form.scope === 'shared' && (
          <Autocomplete
            multiple
            size="small"
            options={staffOptions}
            value={form.members}
            onChange={(_, v) => set('members', v)}
            getOptionLabel={(o) => (o.name ? `${o.name} (${o.email})` : o.email)}
            isOptionEqualToValue={(x, y) => x.email === y.email}
            renderInput={(params) => (
              <TextField {...params} label="共有メンバー (作成者と自分は自動で含まれます)" sx={{ '& .MuiInputBase-root': { bgcolor: '#fff' } }} />
            )}
          />
        )}

        {form.type === 'issue' && (
          <MetaRow label="ステータス">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={form.status}
              onChange={(_, v) => {
                if (v) set('status', v);
              }}
              sx={{ bgcolor: '#fff' }}
            >
              {ISSUE_STATUS_ORDER.map((s) => (
                <ToggleButton key={s} value={s} sx={{ textTransform: 'none', px: 1.5, py: 0.375, fontSize: '0.74rem' }}>
                  {ISSUE_STATUS[s].label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {form.status === 'done' && (
              <Typography variant="caption" color="text.secondary">
                完了は AI へ渡しません
              </Typography>
            )}
          </MetaRow>
        )}

        <MetaRow label="AI へ渡す">
          <FormControlLabel
            control={<Switch size="small" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
            label={<Typography variant="caption">{form.enabled ? '有効' : '無効'}</Typography>}
            sx={{ ml: 0 }}
          />
        </MetaRow>
        {/* 保存バーの高さぶん余白を取り、最下段の項目が隠れないようにする */}
        <Box sx={{ height: 56 }} />
      </Box>
    );
  }

  // --- 閲覧 ----------------------------------------------------------------
  if (!item) {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', color: 'text.secondary', textAlign: 'center', px: 3 }}>
        <Box>
          <Icons.VerticalSplit sx={{ fontSize: 36, color: 'divider' }} />
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.7 }}>
            一覧から定義を選ぶと、ここに内容と編集フォームが出ます
          </Typography>
        </Box>
      </Box>
    );
  }
  if (busy) {
    return <LoadingView message="更新しています..." height="100%" size={36} />;
  }

  const group = item.group_id ? groupById.get(item.group_id) : undefined;
  const injected = isInjected(item);
  const kindLine = injected
    ? `${KIND_LABEL[item.type]} ・ ${charsOf(item).toLocaleString()} 字を注入`
    : item.type === 'workflow'
      ? `${KIND_LABEL[item.type]} ・ 実行時に個別送信 (注入合計に含まれません)`
      : `${KIND_LABEL[item.type]} ・ 注入されません`;
  const audienceText = item.group_id
    ? group
      ? group.scope === 'shared'
        ? `グループ「${group.name}」の共有先 (${formatMembers(group.shared_with)})`
        : `グループ「${group.name}」(個人)`
      : '不明なグループ'
    : item.scope === 'shared'
      ? `共有 (${formatMembers(item.shared_with)})`
      : '自分だけ';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <DetailHeader
        narrow={narrow}
        onBack={onBack}
        kind={kindLine}
        title={item.name}
        actions={
          editable ? (
            <>
              <Tooltip title="編集">
                <IconButton size="small" onClick={onEdit}>
                  <Icons.Edit sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="削除">
                <IconButton size="small" onClick={onDelete} sx={{ '&:hover': { color: 'error.main', bgcolor: '#fdecea' } }}>
                  <Icons.Delete sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="閲覧のみ (編集権限がありません)">
              <Icons.Lock sx={{ fontSize: 16, color: 'text.disabled', mt: 0.75 }} />
            </Tooltip>
          )
        }
      />
      {item.description && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
          {item.description}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <MetaRow label="所属グループ">
          <Chip
            size="small"
            icon={item.group_id ? <Icons.Folder /> : <Icons.FolderOpen />}
            label={item.group_id ? group?.name ?? '不明なグループ' : '共通'}
            sx={{ fontSize: '0.68rem', height: 22, '& .MuiChip-icon': { fontSize: 13 } }}
          />
          <Typography variant="caption" color="text.secondary">
            {item.group_id ? 'このグループのチャットで渡されます' : 'すべてのチャットで渡されます'}
          </Typography>
        </MetaRow>
        <MetaRow label="見える人">
          <Typography variant="caption">{audienceText}</Typography>
          {item.user_id && item.user_id !== currentUserEmail && (
            <Typography variant="caption" color="text.secondary">
              ・ 作成者 {resolve(item.user_id)}
            </Typography>
          )}
        </MetaRow>
        {item.type === 'issue' && (
          <MetaRow label="ステータス">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={item.status ?? 'open'}
              onChange={(_, v) => {
                if (v) onSetStatus(v);
              }}
              disabled={!editable}
              sx={{ bgcolor: '#fff' }}
            >
              {ISSUE_STATUS_ORDER.map((s) => (
                <ToggleButton key={s} value={s} sx={{ textTransform: 'none', px: 1.5, py: 0.375, fontSize: '0.74rem' }}>
                  {ISSUE_STATUS[s].label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </MetaRow>
        )}
        <MetaRow label="AI へ渡す">
          <FormControlLabel
            control={<Switch size="small" checked={item.enabled !== false} disabled={!editable} onChange={onToggleEnabled} />}
            label={<Typography variant="caption">{item.enabled !== false ? '有効' : '無効'}</Typography>}
            sx={{ ml: 0 }}
          />
        </MetaRow>
      </Box>
      <BodyView label={BODY_LABEL[item.type]} body={item.body ?? ''} />
      <Typography variant="caption" color="text.secondary">
        {item.updated_at ? `最終更新 ${formatDateTime(item.updated_at)} ・ ` : ''}
        本文 {(item.body ?? '').length.toLocaleString()} 字
      </Typography>
    </Box>
  );
}
